import { Alert, Stack } from "@mui/material";
import { useTransferLicenseMutation } from "Api/Mutations/Licenses/useTransferLicenseMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { HunterSingleSelectAutocomplete } from "Components/Shared/Inputs/HunterSingleSelectAutocomplete";
import type { HunterSearchDto, PartyLicenseDto } from "Infrastructure/Api/Api";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Licenses.Transfer;

type Props = {
  license: PartyLicenseDto;
  partyPublicID: string;
  isOpen: boolean;
  onClose: () => void;
};

export const TransferLicenseDialog = ({
  license,
  partyPublicID,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useResource();
  const [selectedHunter, setSelectedHunter] = useState<HunterSearchDto | null>(
    null,
  );

  const { mutate: transferLicense, isPending } = useTransferLicenseMutation({
    onSuccess: () => {
      toast.success(t(COMPONENT_RESOURCES.Success));
      setSelectedHunter(null);
      onClose();
    },
    onError: () => toast.error(t(COMPONENT_RESOURCES.Error)),
  });

  const handleSubmit = () => {
    if (!selectedHunter) {
      return;
    }

    transferLicense({
      publicID: license.publicID,
      partyPublicID,
      requestPayload: { targetPartyPublicID: selectedHunter.partyPublicID },
    });
  };

  // Reset the picked target when the dialog is dismissed so a stale selection can't be
  // submitted against a blank search field on reopen.
  const handleClose = () => {
    setSelectedHunter(null);
    onClose();
  };

  return (
    <ModalDialog
      title={t(COMPONENT_RESOURCES.Title, {
        licenseNo: license.licenseNo ?? "",
      })}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <Stack gap={3}>
        <Alert severity="warning">{t(COMPONENT_RESOURCES.Description)}</Alert>
        <HunterSingleSelectAutocomplete
          selectedHunter={selectedHunter}
          onHunterChange={setSelectedHunter}
          label={t(COMPONENT_RESOURCES.TargetLabel)}
          isHunterWithoutLicenseIncluded
          excludedLicensePublicID={license.publicID}
          excludedPartyPublicID={partyPublicID}
          disabled={isPending}
        />
        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          disabled={!selectedHunter}
          isLoading={isPending}
          onClick={handleSubmit}
        >
          {t(COMPONENT_RESOURCES.Submit)}
        </PrimaryButton>
      </Stack>
    </ModalDialog>
  );
};
